import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * `?focus=<id>`: once the page has rendered, brings that field (an element id or data-testid)
 * into view, focuses it and highlights it briefly. Used by the settings search (⌘K).
 */
export function useFocusFromUrl(): void {
  const loc = useLocation();
  useEffect(() => {
    const target = new URLSearchParams(loc.search).get("focus");
    if (!target) return;
    let tries = 0;
    let timer = 0;
    const find = (): HTMLElement | null => document.getElementById(target) ?? document.querySelector<HTMLElement>(`[data-testid="${CSS.escape(target)}"]`);
    const attempt = () => {
      const el = find();
      if (!el) {
        if (++tries < 40) timer = window.setTimeout(attempt, 100);
        return;
      }
      el.scrollIntoView({ block: "center" });
      const focusable = el.matches("input,select,textarea,button,a,[tabindex]") ? el : el.querySelector<HTMLElement>("input,select,textarea,button,a,[tabindex]");
      focusable?.focus({ preventScroll: true });
      el.classList.add("ap-focus-flash");
      // The search dialog gives focus back to where it was opened from as it closes: take it back.
      timer = window.setTimeout(() => {
        if (focusable && !el.contains(document.activeElement)) focusable.focus({ preventScroll: true });
        timer = window.setTimeout(() => el.classList.remove("ap-focus-flash"), 2000);
      }, 400);
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [loc.pathname, loc.search]);
}
